import React, { useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { SelectNewWalletModal } from '@pages/Traders-Room/Balances/Wallets/modals/SelectNewWallet.modal'
import { ReservedFundsBonusModal } from '@pages/Traders-Room/Wallets/modals/ReservedFundsBonus.modal'

import { WalletsExperience } from '@features/wallets'
import { useCryptoWalletInterest } from '@features/wallets/hooks/useCryptoWalletInterest'
import { useWalletRecentActivity } from '@features/wallets/hooks/useWalletRecentActivity'
import { useWalletsPageData } from '@features/wallets/hooks/useWalletsPageData'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionEntity } from '@context/EntityContext'
import { useModal } from '@context/ModalsContext'

import { Body2 } from '@ui/Typography'
import { Accordion } from '@ui/components/Accordion'
import { WarningIcon } from '@ui/icons/Warning.icon'
import { TableNoResults } from '@ui/v2/Table/Table'

import { useAccountActionAccessCheck } from '@utils/AccountAccess/accountAccessCheck'
import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { CommonPaths } from '@utils/path'
import { isZero } from '@utils/validations'
import { WalletRestrictions, isDepositEnabledFromAction, isRestricted } from '@utils/wallet.utils'

import { WalletTypeEnum } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useFundingNav } from '@hooks/useFundingNav'
import { ScrollToIds } from '@hooks/useScrollToElementIds'
import { scrollToTop, useScrollToTop } from '@hooks/useScrollToTop'
import { useTradingAccountStatus } from '@hooks/useTradingAccountStatus'
import { useWithdrawalPageNavigation } from '@hooks/useWithdrawalPageNavigation'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

import { useDepositTracker } from '@analytics/amplitude/deposit'

interface WalletsPageProps {
  walletType: WalletTypeEnum
}

export const WalletsPage: React.FC<WalletsPageProps> = ({ walletType }) => {
  useScrollToTop()

  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const entity = useSessionEntity()
  const { account } = useAccountReadContext()
  const { formatMoney } = useFormatNumber()
  const { navigateToFunding } = useFundingNav()
  const { checkAccountOpenAccountAccess, checkAccountTradersRoomAccess } =
    useAccountActionAccessCheck()
  const { handleWithdrawalNavigation, walletWithdrawLoadingId } = useWithdrawalPageNavigation()
  const {
    hasActiveTradingAccounts,
    hasNegativeBalance,
    isLoading: tradingAccountStatusLoading,
  } = useTradingAccountStatus()
  const tracker = useDepositTracker()
  const addWalletModal = useModal(SelectNewWalletModal)
  const reservedFundsBonusModal = useModal(ReservedFundsBonusModal)
  const cryptoWalletInterest = useCryptoWalletInterest()

  const {
    wallets,
    walletCurrencies,
    selectedWallet,
    selectWallet,
    totalBalance,
    availableCurrencies,
    availableCurrencyIds,
    createWallet,
    isWalletsLoading,
    isTotalBalanceLoading,
    isTotalBalanceError,
    isAvailableCurrenciesLoading,
    isAvailableCurrenciesError,
    isCreatingWallet,
    isError,
  } = useWalletsPageData({ walletType })

  const recentActivity = useWalletRecentActivity({
    room: 'trader',
    walletType,
    walletId: selectedWallet?.id,
    context: 'client',
    historyPath: CommonPaths.TradersRoomTransactionHistory,
  })

  useLayoutEffect(() => {
    if (location.state?.wallets) scrollToTop()
  }, [location])

  const isInitialLoading = isWalletsLoading || isAvailableCurrenciesLoading

  if (isInitialLoading) {
    return <Loading showLoadingIcon fullWidth isLoading />
  }

  if (isError || (!selectedWallet && wallets.length > 0)) {
    return <TableNoResults title={t('errors.Something went wrong')} />
  }

  const isCreateWalletRestricted = isRestricted(
    WalletRestrictions.CREATE_WALLET,
    account?.restrictions
  )
  const addCurrencyDisabled =
    isCreateWalletRestricted || isAvailableCurrenciesError || availableCurrencies.length === 0
  const addCurrencyIconCurrencies =
    availableCurrencyIds.length > 0 ? availableCurrencyIds : walletCurrencies

  const handleAddCurrency = () => {
    checkAccountTradersRoomAccess(() =>
      addWalletModal.open({
        walletTypes: availableCurrencies,
        onSelect: createWallet,
      })
    )
  }

  const walletList = {
    totalBalance: {
      label: t('Wallet.Total'),
      information: t('Wallet.Total available balance description'),
      value: totalBalance ? formatMoney(totalBalance.balance, totalBalance.currency.id) : undefined,
      isLoading: isTotalBalanceLoading,
      errorMessage: isTotalBalanceError ? t('errors.Something went wrong') : undefined,
      subtitle: t('Wallet.Wallet'),
      currencies: walletCurrencies,
    },
    items: wallets.map((wallet) => ({
      id: wallet.id,
      currencyCode: wallet.currency.id,
      currencyName: wallet.currency.name,
      balance: formatMoney(wallet.balance, wallet.currency.id),
      isCurrent: wallet.id === selectedWallet?.id,
      onSelect: () => selectWallet(wallet.id),
    })),
    currentLabel: t('Current'),
    addCurrency: {
      label: t('Add Currency'),
      subtitle: t('Wallet.Wallet'),
      currencies: addCurrencyIconCurrencies,
      disabled: addCurrencyDisabled,
      loading: isCreatingWallet,
      disabledMessage: isCreateWalletRestricted
        ? t('errors.Client is restricted to create a wallet')
        : isAvailableCurrenciesError
        ? t('errors.Something went wrong')
        : t('Wallet.All currencies opened'),
      onSelect: handleAddCurrency,
    },
    addCrypto: cryptoWalletInterest.isVisible
      ? {
          label: t('Wallet.Add Crypto'),
          subtitle: t('Wallet.Wallet'),
          disabled: cryptoWalletInterest.isRegistered,
          onSelect: cryptoWalletInterest.open,
        }
      : undefined,
  }

  if (!selectedWallet) {
    return (
      <Box id={ScrollToIds.WalletsHeader}>
        <WalletsExperience
          walletList={walletList}
          emptyState={{
            title: t('Wallet.No wallets title'),
            description: t('Wallet.No wallets description'),
          }}
        />
      </Box>
    )
  }

  const isDepositDisabled =
    isRestricted(WalletRestrictions.DEPOSIT, selectedWallet.restrictions) ||
    !isDepositEnabledFromAction(selectedWallet.depositAction)
  const isTransferDisabled = isRestricted(
    WalletRestrictions.TRANSFER_FROM,
    selectedWallet.restrictions
  )
  const isWithdrawDisabled =
    isRestricted(WalletRestrictions.WITHDRAW, selectedWallet.restrictions) ||
    hasNegativeBalance ||
    selectedWallet.balance < 0
  const isWithdrawLoading =
    tradingAccountStatusLoading || walletWithdrawLoadingId === selectedWallet.id
  const showReservedBonus =
    entity === TickmillCompaniesEnum.TICKMILL_SC || entity === TickmillCompaniesEnum.TICKMILL_UAE
  const showAccountReviewAlert =
    entity === TickmillCompaniesEnum.TICKMILL_SC &&
    walletType === WalletTypeEnum.CFD &&
    wallets.some((wallet) => isRestricted(WalletRestrictions.WITHDRAW, wallet.restrictions))

  const handleTransfer = () => {
    checkAccountOpenAccountAccess(() => {
      if (!hasActiveTradingAccounts && wallets.length === 1) {
        navigate(CommonPaths.NewLiveTradingAccount)
        return
      }
      navigateToFunding(`${CommonPaths.TradersRoomWallets}/${selectedWallet.id}/transfer`)
    })
  }

  return (
    <Box id={ScrollToIds.WalletsHeader} flex direction='column' gap='x24'>
      {showAccountReviewAlert && (
        <Accordion
          fullWidth
          title={t('Wallet.Account review in progress')}
          leftAddon={<WarningIcon size={24} color='text-warning-high-em' />}
        >
          <Body2 color='text-med-em'>{t('Wallet.Account review description')}</Body2>
        </Accordion>
      )}
      <WalletsExperience
        currentWallet={{
          balanceLabel: t('Wallet.Available Balance'),
          balance: formatMoney(selectedWallet.balance, selectedWallet.currency.id),
          currencyCode: selectedWallet.currency.id,
          walletNumber: selectedWallet.name,
          information: t('Wallet.Trader wallet description'),
          copyWalletNumberLabel: t('Copy wallet number'),
          breakdown: [
            {
              id: 'reserved',
              label: t('Wallet.Reserved'),
              value: formatMoney(selectedWallet.reserved, selectedWallet.currency.id),
            },
            ...(showReservedBonus
              ? [
                  {
                    id: 'bonus-reserved',
                    label: t('Wallet.Reserved funds for Bonus'),
                    value: formatMoney(selectedWallet.bonusReserved, selectedWallet.currency.id),
                    onSelect: !isZero(selectedWallet.bonusReserved)
                      ? () => reservedFundsBonusModal.open({ walletId: selectedWallet.id })
                      : undefined,
                  },
                ]
              : []),
          ],
          actions: {
            items: [
              {
                kind: 'deposit',
                label: t('Deposit'),
                disabled: isDepositDisabled,
                onClick: () => {
                  tracker.start({ entry_point: 'wallet', preset_wallet_id: selectedWallet.id })
                  navigateToFunding(`${CommonPaths.Deposit}/wallet`, {
                    state: { selectedWalletId: selectedWallet.id },
                  })
                },
              },
              {
                kind: 'transfer',
                label: t('Transfer'),
                disabled: isTransferDisabled,
                onClick: handleTransfer,
              },
              {
                kind: 'withdraw',
                label: t('Withdraw'),
                disabled: isWithdrawDisabled,
                loading: isWithdrawLoading,
                onClick: () =>
                  checkAccountTradersRoomAccess(() =>
                    handleWithdrawalNavigation({ walletId: selectedWallet.id })
                  ),
              },
            ],
            primaryAction: 'deposit',
            mobileOrder: ['withdraw', 'deposit', 'transfer'],
            menuLabel: t('Wallet actions'),
            actionUnavailableMessage: t('Wallet.Action unavailable'),
            walletUnavailableMessage: t('Wallet.Transactions unavailable'),
          },
        }}
        walletList={walletList}
        recentActivity={recentActivity}
      />
    </Box>
  )
}
